import React, { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "../../components/ui/Card";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../../components/ui/Accordion";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
    Calendar,
    Trophy,
    Users,
    Clock,
    Zap,
    AlertCircle,
    Star,
    Award,
} from "lucide-react";
import { Separator } from "../../components/ui/Separator";
import { httpsCallable } from "firebase/functions";
import { Timestamp } from "firebase/firestore";
import { functions } from "src/api/firebase";
import StartContestCard from "./startContestCard";
import { useAuth } from "src/contexts/AuthContext";
import { FAQ, InterviewCase, Rule } from "./types";

// Import the WeeklyContest type
interface WeeklyContestResponse {
    status: string;
    message: string;
    data: {
        id: string;
        caseId: number;
        title: string;
        weekNumber: number;
        description: string;
        startTime:
            | {
                  _seconds: number;
                  _nanoseconds: number;
              }
            | Timestamp;
        endTime:
            | {
                  _seconds: number;
                  _nanoseconds: number;
              }
            | Timestamp;
        isActive: boolean;
        participants: {
            [userId: string]: {
                sessionId: string;
            };
        };
        userParticipated: boolean;
        topParticipants: any[];
        caseData: any;
    };
}

const NormalWeeklyContestPage: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [contestData, setContestData] = useState<
        WeeklyContestResponse["data"] | null
    >(null);

    useEffect(() => {
        const fetchContestData = async () => {
            try {
                setLoading(true);
                const getWeeklyContest = httpsCallable(
                    functions,
                    "getWeeklyContest"
                );
                const response = await getWeeklyContest();

                // Extract contest data from the response
                const responseData = response.data as WeeklyContestResponse;
                setContestData(responseData.data);
                console.log("Weekly contest data:", responseData);
            } catch (err) {
                console.error("Error fetching weekly contest:", err);
                setError(
                    "Failed to load contest data. Please try again later."
                );
            } finally {
                setLoading(false);
            }
        };

        fetchContestData();
    }, []);

    // Convert to Firebase Timestamp if needed
    const toTimestamp = (timestamp: any): Timestamp => {
        if (!timestamp) return new Timestamp(0, 0);

        // If it's already a Timestamp instance
        if (timestamp instanceof Timestamp) return timestamp;

        // If it's a serialized Timestamp with _seconds and _nanoseconds
        if (
            timestamp._seconds !== undefined &&
            timestamp._nanoseconds !== undefined
        ) {
            return new Timestamp(timestamp._seconds, timestamp._nanoseconds);
        }

        // Fallback - convert from seconds if available
        if (typeof timestamp === "number") {
            return Timestamp.fromMillis(timestamp * 1000);
        }

        return new Timestamp(0, 0);
    };

    // Format timestamp to readable date
    const formatDate = (timestamp: any) => {
        if (!timestamp) return "";

        const firestoreTimestamp = toTimestamp(timestamp);
        const date = firestoreTimestamp.toDate();

        return date.toLocaleDateString("en-US", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    };

    // Format timestamp to readable time
    const formatTime = (timestamp: any) => {
        if (!timestamp) return "";

        const firestoreTimestamp = toTimestamp(timestamp);
        const date = firestoreTimestamp.toDate();

        return date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            timeZoneName: "short",
        });
    };

    // Calculate days remaining
    const calculateDaysRemaining = () => {
        if (!contestData?.endTime) return "TBD";

        const firestoreTimestamp = toTimestamp(contestData.endTime);
        const endDate = firestoreTimestamp.toDate();
        const now = new Date();
        const diffTime = endDate.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return "Ended";
        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "1 Day";
        return `${diffDays} Days`;
    };

    // Determine contest status based on time
    const determineContestStatus = () => {
        if (!contestData)
            return { isUpcoming: false, isActive: false, isEnded: false };

        const now = new Date();
        const startDate = toTimestamp(contestData.startTime).toDate();
        const endDate = toTimestamp(contestData.endTime).toDate();

        const isUpcoming = now < startDate;
        const isActive = now >= startDate && now <= endDate;
        const isEnded = now > endDate;

        return { isUpcoming, isActive, isEnded };
    };

    const contestStatus = determineContestStatus();

    // Calculate the number of participants
    const participantsCount =
        contestData?.participants && contestData.participants !== null
            ? Object.keys(contestData.participants).length + 29
            : 0;

    const rules: Rule[] = [
        {
            id: "rule1",
            text: "🏆 This is a consulting case competition where participants will solve a business case and provide recommendations.",
        },
        {
            id: "rule2",
            text: "📝 Each week, one case will be selected from our database of 20 consulting cases.",
        },
        {
            id: "rule3",
            text: "⚠️ Each participant gets only ONE submission attempt - please prepare thoroughly before submitting!",
        },
        {
            id: "rule4",
            text: "🔍 Submissions will be scored based on structured thinking, analytical approach, and quality of recommendations.",
        },
        {
            id: "rule5",
            text: "💰 The highest scoring participant will win $30 (≈200 RMB) as the championship prize.",
        },
        {
            id: "rule6",
            text: "🤝 In case of a tie, judges will manually review the quality of recommendations to determine the winner.",
        },
        {
            id: "rule7",
            text: "⏰ Contest runs from Friday 6PM to Sunday 6PM (PT) each week.",
        },
        {
            id: "rule8",
            text: "📊 All submissions must include a structured framework, data analysis, and clear recommendations.",
        },
        {
            id: "rule9",
            text: "📱 By participating, you agree to allow WeKruit to showcase parts of your solution for educational purposes.",
        },
    ];

    const faqs: FAQ[] = [
        {
            id: "faq1",
            question: "What is a consulting case interview?",
            answer: "A consulting case interview simulates a business problem that clients might face. Participants analyze the situation, structure their approach, and provide recommendations to solve the problem. Our weekly contest uses real-world business scenarios to test these skills.",
        },
        {
            id: "faq2",
            question: "How do I submit my solution?",
            answer: "Log in to your WeKruit account, navigate to the Weekly Contest page, and click the 'Start contest' button. You'll be guided to a case interview session.",
        },
        {
            id: "faq3",
            question: "How are submissions scored?",
            answer: "Submissions are evaluated on multiple criteria: problem structuring (30%), analytical rigor (30%), creativity of solutions (20%), and clarity of recommendations (20%). Our automated system provides initial scoring, with final reviews by experienced consultants.",
        },
        {
            id: "faq4",
            question: "Why do we only get one submission attempt?",
            answer: "This simulates the real consulting interview environment where you get one chance to impress the interviewer. It encourages thorough preparation and thoughtful analysis rather than trial-and-error approaches.",
        },
        {
            id: "faq5",
            question: "What should I include in my recommendation section?",
            answer: "Your recommendations should be clear, actionable, and backed by your analysis. Include specific steps the client should take, potential implementation challenges, and expected outcomes. High-quality recommendations can be the deciding factor in tiebreaker situations.",
        },
        {
            id: "faq6",
            question: "When and how will winners be announced?",
            answer: "Winners will be announced every Monday after the contest ends. Results will be published on the WeKruit platform and winners will be notified by email. Prize distribution will occur within 7 business days.",
        },
        {
            id: "faq7",
            question:
                "Can I practice cases before participating in the contest?",
            answer: "Yes! We encourage you to practice with our database of 20 cases available on the WeKruit platform. This will help you become familiar with our format and expectations before the contest.",
        },
        {
            id: "faq8",
            question:
                "I'm having technical issues with my submission. What should I do?",
            answer: "Please contact our support team at admin1@wekruit.com with a screenshot of the issue and a detailed description. Given the time-sensitive nature of the contest, we recommend reaching out at least 6 hours before the deadline.",
        },
    ];

    // Display loading state
    if (loading) {
        return (
            <div className="container mx-auto py-8 max-w-4xl text-center">
                <h2 className="text-3xl font-bold mb-4">
                    Loading Contest Data...
                </h2>
                <p className="text-lg">
                    Please wait while we fetch the latest contest information.
                </p>
            </div>
        );
    }

    // Display error state
    if (error) {
        return (
            <div className="container mx-auto py-8 max-w-4xl">
                <Alert variant="destructive" className="text-lg">
                    <AlertCircle className="h-5 w-5" />
                    <AlertTitle className="text-xl">Error</AlertTitle>
                    <AlertDescription className="text-lg">
                        {error}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="container mx-auto py-8 max-w-4xl">
            {/* Hero Section with Enhanced Typography */}
            <div className="text-center mb-10 bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-lg shadow-sm">
                <Badge className="mb-4 text-lg py-1 px-4">
                    Week {contestData?.weekNumber || 1}
                </Badge>
                <h1 className="text-5xl font-extrabold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-[var(--color-primary)] to-indigo-600">
                    {contestData?.title || "Weekly Consulting Challenge"}
                </h1>
                <p className="text-2xl text-muted-foreground">
                    Solve real business cases and win amazing prizes!
                </p>
            </div>

            {/* Replace description alert with InterviewCaseCard */}
            {contestData && (
                <div className="mb-10">
                    <StartContestCard
                        contestId={contestData.id}
                        interviewCase={contestData.caseData}
                        onGoing={contestStatus.isActive}
                        hasDone={contestData.userParticipated ?? false}
                        participantNumber={participantsCount}
                        startTime={contestData.startTime}
                        endTime={contestData.endTime}
                    />
                    {/* Display the description below if needed */}
                    {contestData.description && (
                        <Alert className="mt-6 border-2 border-blue-200">
                            <Zap className="h-6 w-6 text-blue-600" />
                            <AlertTitle className="text-xl font-bold">
                                Additional Information
                            </AlertTitle>
                            <AlertDescription className="text-lg mt-2">
                                {contestData.description}
                            </AlertDescription>
                        </Alert>
                    )}
                </div>
            )}

            {/* Key Information Cards - Larger and More Prominent */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
                <Card className="shadow-md hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-gradient-to-r from-amber-50 to-amber-100">
                        <CardTitle className="text-lg font-bold">
                            Prize Pool
                        </CardTitle>
                        <Trophy className="h-6 w-6 text-amber-500" />
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="text-3xl font-bold text-amber-600">
                            $30
                        </div>
                        <p className="text-base text-muted-foreground mt-2">
                            Winner takes all! Approximately 200 RMB
                        </p>
                    </CardContent>
                </Card>
                <Card className="shadow-md hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-gradient-to-r from-blue-50 to-blue-100">
                        <CardTitle className="text-lg font-bold">
                            {contestStatus.isUpcoming
                                ? "Next Contest"
                                : "Participants"}
                        </CardTitle>
                        {contestStatus.isUpcoming ? (
                            <Calendar className="h-6 w-6 text-blue-500" />
                        ) : (
                            <Users className="h-6 w-6 text-blue-500" />
                        )}
                    </CardHeader>
                    <CardContent className="pt-6">
                        {contestStatus.isUpcoming ? (
                            <>
                                <div className="text-xl font-bold text-blue-600">
                                    {formatDate(contestData?.startTime)}
                                </div>
                                <p className="text-base text-muted-foreground mt-2">
                                    Starting at 6:00 PM PT
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="text-3xl font-bold text-blue-600">
                                    {participantsCount}
                                </div>
                                <p className="text-base text-muted-foreground mt-2">
                                    Talented consultants joined this week
                                </p>
                            </>
                        )}
                    </CardContent>
                </Card>
                <Card className="shadow-md hover:shadow-lg transition-shadow duration-300">
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 bg-gradient-to-r from-red-50 to-red-100">
                        <CardTitle className="text-lg font-bold">
                            {contestStatus.isUpcoming
                                ? "Get Ready!"
                                : "Time Left"}
                        </CardTitle>
                        <Clock className="h-6 w-6 text-red-500" />
                    </CardHeader>
                    <CardContent className="pt-6">
                        {contestStatus.isUpcoming ? (
                            <>
                                <div className="text-xl font-bold text-red-600">
                                    Coming Soon
                                </div>
                                <p className="text-base text-muted-foreground mt-2">
                                    Prepare for the next challenge!
                                </p>
                            </>
                        ) : (
                            <>
                                <div className="text-3xl font-bold text-red-600">
                                    {calculateDaysRemaining()}
                                </div>
                                <p className="text-base text-muted-foreground mt-2">
                                    Deadline:{" "}
                                    <span className="font-semibold">
                                        {formatDate(contestData?.endTime)}
                                    </span>
                                    , 6:00 PM PT
                                </p>
                            </>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Status Badge - More Prominent */}
            <div className="text-center mb-10">
                <Badge
                    className="text-xl py-2 px-6 shadow-md"
                    variant={
                        contestStatus.isActive
                            ? "default"
                            : contestStatus.isUpcoming
                            ? "secondary"
                            : "outline"
                    }>
                    {contestStatus.isUpcoming
                        ? "🔜 Upcoming Contest"
                        : contestStatus.isActive
                        ? "🔴 LIVE: Contest Active Now!"
                        : "⏸️ Contest Ended"}
                </Badge>
            </div>

            {/* Rules Card - Enhanced Typography */}
            <Card className="mb-10 shadow-lg border-l-4 border-l-blue-500">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-transparent">
                    <CardTitle className="text-2xl font-extrabold flex items-center">
                        <Award className="h-6 w-6 mr-2 text-blue-600" />
                        Contest Rules
                    </CardTitle>
                    <CardDescription className="text-lg">
                        Please read carefully before submitting your entry
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                    <ul className="space-y-4">
                        {rules.map((rule) => (
                            <li
                                key={rule.id}
                                className="flex items-start text-lg">
                                <span className="mr-3 flex-shrink-0 mt-1 text-blue-500 font-bold">
                                    {rule.id.includes("3") ||
                                    rule.id.includes("5") ||
                                    rule.id.includes("7")
                                        ? "→"
                                        : "•"}
                                </span>
                                <span
                                    className={
                                        rule.id.includes("3") ||
                                        rule.id.includes("5") ||
                                        rule.id.includes("7")
                                            ? "font-bold"
                                            : ""
                                    }>
                                    {rule.text}
                                </span>
                            </li>
                        ))}
                    </ul>
                </CardContent>
            </Card>

            {/* How to Participate Card - Enhanced */}
            <Card className="mb-10 shadow-lg border-l-4 border-l-green-500">
                <CardHeader className="bg-gradient-to-r from-green-50 to-transparent">
                    <CardTitle className="text-2xl font-extrabold flex items-center">
                        <Star className="h-6 w-6 mr-2 text-green-600" />
                        How to Participate
                    </CardTitle>
                    <CardDescription className="text-lg">
                        Follow these steps to enter the weekly contest
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                    <ol className="list-decimal ml-8 space-y-4">
                        {[
                            "Sign in to your account (or register if you're new)",
                            "Review this week's case and requirements carefully",
                            "Structure your approach to the consulting case",
                            "Prepare your analysis and recommendations",
                            'Click the "Start Contest" button to start interview, make sure you click generate report to attend',
                            "Wait for the results to be announced after the deadline!",
                        ].map((step, index) => (
                            <li key={index} className="text-lg">
                                <span
                                    className={
                                        index === 2 || index === 3
                                            ? "font-bold"
                                            : ""
                                    }>
                                    {step}
                                </span>
                            </li>
                        ))}
                    </ol>
                </CardContent>
            </Card>

            {/* Important Dates Card - Enhanced */}
            <Card className="mb-10 shadow-lg border-l-4 border-l-purple-500">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-transparent">
                    <CardTitle className="text-2xl font-extrabold flex items-center">
                        <Calendar className="h-6 w-6 mr-2 text-purple-600" />
                        Important Dates
                    </CardTitle>
                    <CardDescription className="text-lg">
                        Mark your calendar with these key contest dates
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                    <div className="space-y-6">
                        <div className="flex items-start p-4 rounded-lg bg-gradient-to-r from-purple-50 to-transparent">
                            <Calendar className="h-8 w-8 mr-4 mt-1 text-purple-500" />
                            <div>
                                <p className="font-bold text-xl">
                                    {formatDate(contestData?.startTime)}
                                </p>
                                <p className="text-lg text-muted-foreground">
                                    Contest starts at 6:00 PM PT
                                </p>
                            </div>
                        </div>
                        <Separator className="h-0.5 bg-purple-100" />
                        <div className="flex items-start p-4 rounded-lg bg-gradient-to-r from-red-50 to-transparent">
                            <Calendar className="h-8 w-8 mr-4 mt-1 text-red-500" />
                            <div>
                                <p className="font-bold text-xl">
                                    {contestData?.startTime
                                        ? (() => {
                                              const startTimestamp =
                                                  toTimestamp(
                                                      contestData.startTime
                                                  );
                                              const startDate =
                                                  startTimestamp.toDate();
                                              const submissionDate = new Date(
                                                  startDate.getTime() +
                                                      2 * 86400000
                                              );
                                              return submissionDate.toLocaleDateString(
                                                  "en-US",
                                                  {
                                                      weekday: "long",
                                                      year: "numeric",
                                                      month: "long",
                                                      day: "numeric",
                                                  }
                                              );
                                          })()
                                        : "TBD"}
                                </p>
                                <p className="text-lg text-muted-foreground">
                                    <span className="font-semibold text-red-600">
                                        Submission deadline (6:00 PM PT)
                                    </span>
                                </p>
                            </div>
                        </div>
                        <Separator className="h-0.5 bg-purple-100" />
                        <div className="flex items-start p-4 rounded-lg bg-gradient-to-r from-green-50 to-transparent">
                            <Calendar className="h-8 w-8 mr-4 mt-1 text-green-500" />
                            <div>
                                <p className="font-bold text-xl">
                                    {contestData?.startTime
                                        ? (() => {
                                              const startTimestamp =
                                                  toTimestamp(
                                                      contestData.startTime
                                                  );
                                              const startDate =
                                                  startTimestamp.toDate();
                                              const announcementDate = new Date(
                                                  startDate.getTime() +
                                                      3 * 86400000
                                              );
                                              return announcementDate.toLocaleDateString(
                                                  "en-US",
                                                  {
                                                      weekday: "long",
                                                      year: "numeric",
                                                      month: "long",
                                                      day: "numeric",
                                                  }
                                              );
                                          })()
                                        : "TBD"}
                                </p>
                                <p className="text-lg text-muted-foreground">
                                    Winners announced, new case revealed
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* FAQ Card - Enhanced */}
            <Card className="shadow-lg border-l-4 border-l-amber-500">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-transparent">
                    <CardTitle className="text-2xl font-extrabold flex items-center">
                        <Zap className="h-6 w-6 mr-2 text-amber-600" />
                        Frequently Asked Questions
                    </CardTitle>
                    <CardDescription className="text-lg">
                        Find answers to common questions about the contest
                    </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                    <Accordion type="single" collapsible className="w-full">
                        {faqs.map((faq) => (
                            <AccordionItem key={faq.id} value={faq.id}>
                                <AccordionTrigger className="text-lg font-medium py-4">
                                    {faq.question}
                                </AccordionTrigger>
                                <AccordionContent className="text-base pt-2 pb-4">
                                    {faq.answer}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </CardContent>
                <CardFooter className="flex justify-between p-6 bg-gradient-to-r from-amber-50 to-transparent">
                    <p className="text-base text-muted-foreground">
                        Still have questions?
                    </p>
                    <Button variant="outline" className="text-base">
                        Contact admin1@wekruit.com
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
};

export default NormalWeeklyContestPage;
